import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'; // To read the query parameter from URL
import bg1 from '../assets/images/backgrounds/rentic_bg_element1.svg';
import bg3 from '../assets/images/backgrounds/rentic_bg_element3.svg';
import { Spinner } from '../components/common/Spinner';
import { ProductEditResponse, ProductListResponse } from '../api/requests/interfaces/Product';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import { ButtonArrowForward } from '../components/buttons/ButtonArrowForward';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import { confirmPayment, getEstoPaymentStatus, getStripePaymentStatus } from '../api/requests/payment';
import { CheckoutPricesResponse, PAYMENT_STATUS, PaymentCompletedPagePaymentType } from '../api/requests/interfaces/Payment';
import { getProductDetails, getProductDetailsEdit } from '../api/requests/product';
import { StripeCheckoutStatus } from "@stripe/stripe-js";
import { format } from 'date-fns';
import { et } from 'date-fns/locale';
import ReactGA from 'react-ga4';

const PaymentCompletedPage = () => {
  const [paymentStatus, setPaymentStatus] = useState<PAYMENT_STATUS | undefined>(undefined);
  const [searchParams, setSearchParams] = useSearchParams();
  const [paymentVerificationMade, setPaymentVerificationMade] = useState(false);
  const [loadingPayment, setLoadingPayment] = useState(true);
  const [loadingProductData, setLoadingProductData] = useState(false);
  const [orderData, setOrderData] = useState<{ productId: number; activeUntilDate?: Date } | undefined>(undefined);
  const [error, setError] = useState<string>("");

  const [productData, setProductData] = useState<ProductListResponse | undefined>(undefined);
  const [productEditData, setProductEditData] = useState<ProductEditResponse | undefined>(undefined);

  const navigate = useNavigate();

  /* useEffect(() => {
    if (orderData) {
      getDetails().then((productDetails) => {
        setProductEditData(productDetails);
      });
    }
  }, [orderData]) */

  useEffect(() => {
    window.scrollTo(0, 0);
    // const orderToken = searchParams.get('order-token');
    const paymentType = searchParams.get('payment_type') as PaymentCompletedPagePaymentType;
    const sessionId = searchParams.get('session_id'); // Stripe session id to confirm the payment status
    const orderId = searchParams.get('order_id');
    const productId = searchParams.get('product_id');
    // if (orderToken && orderId && !paymentVerificationMade) {
    //   // Send the orderToken to the backend for verification
    //   verifyPayment(Number(orderId), orderToken);
    // }
    if (!paymentVerificationMade && paymentType !== PaymentCompletedPagePaymentType.FREE) {
      let subscriptionId;
      if (paymentType === PaymentCompletedPagePaymentType.ESTO) {
        subscriptionId = orderId;
      } else {
        subscriptionId = sessionId;
      }
      checkOrderStatus(subscriptionId!, paymentType);
    } else if (!paymentVerificationMade && paymentType === PaymentCompletedPagePaymentType.FREE) {
      setLoadingPayment(false);
      // when it was total 0 purcahse, we can already set the data
      const productIdNum = Number(productId);
      setOrderData({activeUntilDate: undefined, productId: productIdNum});
      setPaymentStatus(PAYMENT_STATUS.PAYMENT_SUCCESS);
      setPaymentVerificationMade(true);
    }
  }, [searchParams]);

  useEffect(() => {
    // if payment status changes to success, fetch product details to show completed order
    if (paymentStatus === PAYMENT_STATUS.PAYMENT_SUCCESS) {

      ReactGA.event({
        category: 'ItemEditPage/PaymentCompletedPage',
        action: 'Kuulutus makstud'
      });

      fetchCompletedOrderData();
    }
  }, [paymentStatus])

  const fetchCompletedOrderData = async () => {
    if (orderData) {
      setLoadingProductData(true);
      const data = await getProductDetails(orderData!.productId);
      if (orderData) {
        setOrderData({...orderData, activeUntilDate: data.availableUntilDate});
      }
      setLoadingProductData(false);
      setProductData(data);
    }
  }

  const verifyPayment = async (orderId: number, orderToken: string) => {
    setLoadingPayment(true);
    try {
      console.log("PRODUCT CHECKOUT DATA: " + JSON.stringify(productEditData?.checkoutOverviewData));
      const response = await confirmPayment(orderId, orderToken);
      setPaymentStatus(response.status);
      setOrderData({ activeUntilDate: response.visibleUntilDate!, productId: response.productId! })
      setLoadingPayment(false);
      setPaymentVerificationMade(true);
    } catch (err) {
      setError('Error verifying payment');
      setLoadingPayment(false);
      console.error(err);
    }
  };
  const getDetails = async () => {
    const productDetails = await getProductDetailsEdit(orderData!.productId);
    return productDetails;
  };

  const checkOrderStatus = async (sessionId: string, paymentType: PaymentCompletedPagePaymentType) => {
    setLoadingPayment(true);
    try {
      let response;
      if (paymentType === PaymentCompletedPagePaymentType.STRIPE) {
        response = await getStripePaymentStatus(sessionId);
      } else {
        response = await getEstoPaymentStatus(sessionId);
      }
      setPaymentStatus(response.status);
      setOrderData({ activeUntilDate: response.visibleUntilDate!, productId: response.productId! })
      setLoadingPayment(false);
      setPaymentVerificationMade(true);
    } catch (err) {
      setError('Error verifying payment');
      setLoadingPayment(false);
      console.error(err);
    }
  }

  return (
    <div className="w-full h-full flex justify-center items-center overflow-hidden">
      <img className="absolute top-0 left-0 h-[90%] opacity-35 w-auto pointer-events-none" src={bg1} alt="" />
      <img className="absolute top-0 right-0 h-[50%] opacity-25 object-contain object-top w-auto pointer-events-none" src={bg3} alt="" />
      <div className="mt-[60px] pb-10 flex flex-col gap-3 justify-center">
        {loadingPayment ?
          <div className='flex flex-col items-center gap-10'>
            <h2>Makse töötlemine</h2>
            <Spinner />
          </div>
          :
          paymentStatus === PAYMENT_STATUS.PAYMENT_SUCCESS ?
            <div className='flex gap-6 px-5 lg:px-10 w-full'>
              <div className='flex flex-col gap-4 justify-center max-w-[800px]'>
                {
                  loadingProductData &&
                  <Spinner />
                }
                {
                  productData && !loadingProductData &&
                  <>
                    <div className='flex text-left justify-between items-center'>
                      <h1>Kuulutus lisatud!</h1>
                      <div>
                        <ButtonArrowForward title='Mine kuulutuse lehele' onClick={() => { navigate("/detail/" + productData.id) }} icon={<ArrowOutwardIcon />} iconPosition='right' />
                      </div>
                    </div>
                    <div className='relative grid lg:grid-cols-2 gap-4 bg-white/80 z-10 shadow-main rounded-xl p-4 lg:p-10'>
                      <div className='absolute h-[80px] w-[80px] lg:h-[140px] lg:w-[140px] z-20 bottom-[-30px] lg:bottom-[50px] right-0 lg:right-[-50px] rotate-12 text-primary'>
                        <VerifiedOutlinedIcon style={{ height: "100%", width: "100%", borderRadius: "50%", backgroundColor: "white", opacity: "0.8" }} />
                      </div>
                      {/* <ListingCard data={dummyData} /> */}
                      <div className='flex flex-row w-full'>
                        <div className='flex-none'>
                          <img src={productData.defaultPictureUrl} className='h-[200px] w-[200px] rounded-lg object-contain' />
                        </div>
                        <div className='flex flex-col justify-start w-full flex-1 pl-3 items-start'>
                          <div className='text-2xl font-bold w-full text-left'>{productData.title}</div>
                          <div className='text-lg font-bold'>{productData.dailyPrice} €/päev</div>
                          {
                            orderData?.activeUntilDate &&
                            <div className='mt-1 text-left'>Nähtaval: Praegu - {format(orderData.activeUntilDate, 'dd. MMM yyyy', { locale: et })}</div>
                          }
                        </div>
                      </div>
                    </div>
                  </>
                }
              </div>
            </div>
            :
            (paymentStatus === PAYMENT_STATUS.PAYMENT_UNPAID ?
              <div className='text-orange-500 text-xl'>Makse on veel teostamata</div>
              :
              <div className='text-red-500 text-xl'>Makse vormistamisel esines viga</div>)
        }
      </div>
    </div>

  );
};

export default PaymentCompletedPage;
